if (!customElements.get('td-copy-link')) {
  const SELECTORS = {
    button: '[data-td-copy-button]',
    label: '[data-td-copy-label]',
    status: '[data-td-copy-status]',
  };
  const RESET_DELAY = 2000;

  class TdCopyLink extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.resetTimer = null;
      this.statusFrame = null;
    }

    connectedCallback() {
      // A reconnect must not inherit a pending reset or status update.
      this.cancelPending();
      // No Clipboard API (insecure context, old browser): hide rather than
      // leave a button that does nothing.
      if (!navigator.clipboard) {
        this.hidden = true;
        return;
      }
      this.button = this.querySelector(SELECTORS.button);
      this.label = this.querySelector(SELECTORS.label);
      this.status = this.querySelector(SELECTORS.status);
      this.defaultText = this.label ? this.label.textContent : '';
      this.button?.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleClick);
      this.cancelPending();
      // Restore now: a move within the reset window would otherwise reconnect
      // and capture "Copied" as the default label.
      this.reset();
    }

    cancelPending() {
      if (this.resetTimer) {
        clearTimeout(this.resetTimer);
        this.resetTimer = null;
      }
      if (this.statusFrame) {
        cancelAnimationFrame(this.statusFrame);
        this.statusFrame = null;
      }
    }

    reset() {
      if (this.label) this.label.textContent = this.defaultText;
      if (this.status) this.status.textContent = '';
    }

    async handleClick() {
      let message = this.label?.dataset.tdCopiedText || '';
      try {
        await navigator.clipboard.writeText(this.dataset.tdCopyUrl);
      } catch (error) {
        // Permission denied (e.g. the theme editor iframe): say so instead of doing nothing.
        message = this.label?.dataset.tdFailedText || '';
      }
      this.announce(message);
    }

    announce(message) {
      this.cancelPending();
      if (this.label) this.label.textContent = message;
      if (this.status) {
        // Clear first so a repeat copy is announced again.
        this.status.textContent = '';
        this.statusFrame = requestAnimationFrame(() => {
          this.statusFrame = null;
          this.status.textContent = message;
        });
      }
      this.resetTimer = setTimeout(() => this.reset(), RESET_DELAY);
    }
  }

  customElements.define('td-copy-link', TdCopyLink);
}

if (!customElements.get('td-instagram-share')) {
  // Instagram has no web share URL. The markup is a link to the brand profile;
  // on a touch device with the Web Share API it becomes a share button that
  // opens the phone's share sheet, where the reader picks Instagram. Desktop
  // share sheets rarely list Instagram, so desktop keeps the profile link.
  class TdInstagramShare extends HTMLElement {
    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
    }

    connectedCallback() {
      this.button = this.querySelector('[data-td-share-button]');
      if (!this.button) return;
      this.useShareSheet = Boolean(navigator.share) && window.matchMedia('(pointer: coarse)').matches;
      if (!this.useShareSheet) {
        // No share sheet and no profile URL: nothing useful to do.
        this.hidden = !this.button.hasAttribute('href');
        return;
      }
      this.hidden = false;
      this.button.setAttribute('aria-label', this.button.dataset.tdShareLabel);
      this.button.setAttribute('role', 'button');
      this.button.addEventListener('click', this.handleClick);
    }

    disconnectedCallback() {
      this.button?.removeEventListener('click', this.handleClick);
    }

    async handleClick(event) {
      event.preventDefault();
      try {
        await navigator.share({ title: this.dataset.tdShareTitle, url: this.dataset.tdShareUrl });
      } catch (error) {
        // Closing the sheet is a normal outcome; any other failure falls back to the profile.
        if (error.name !== 'AbortError' && this.button.href) window.open(this.button.href, '_blank', 'noopener');
      }
    }
  }

  customElements.define('td-instagram-share', TdInstagramShare);
}
